import template from './ko-tabs-dashboard.html';

/**
 * ViewModel for the Tabs Dashboard component
 * @class TabsDashboardComponentViewModel
 */
/**
 * Creates a new Tabs Dashboard component
 * @param {Object} params - The component parameters
 * @param {string} [params.tabs_title='Tabs Dashboard'] - The title displayed at the top of the dashboard
 * @param {string} params.tabs_source - The source registry key to get the tab components from
 * @param {Object} [params.item=null] - The item to pass to each tab component
 * @property {Object} tab_component - The tab component configuration
 * @property {string} tab_component.component - The component name
 * @property {number} tab_component.idx - The tab index
 * @property {string} tab_component.icon - The tab icon
 * @example
 *
 */
class TabsDashboardComponentViewModel
{

	constructor(params)
	{
		this.params = params;
		this.tabs_title = ko_helper.safe_observable(params.tabs_title || 'Tabs Dashboard');
		this.tabs_source = ko_helper.safe_observable(params.tabs_source);
		this.item = ko_helper.safe_observable(params.item ?? null);
		this.tabs = ko.observableArray([]);
		this.selected_tab = ko.observable();
		this.dropdown_open = ko.observable(false);

		this.inline_tabs = ko.pureComputed(() =>
			this.tabs().filter(tab => !this.is_side_dropdown(tab?.tab_component))
		);

		this.dropdown_tabs = ko.pureComputed(() =>
			this.tabs().filter(tab => this.is_side_dropdown(tab?.tab_component))
		);

		this.dropdown_tabs_visible = ko.pureComputed(() => this.dropdown_tabs().filter(tab => this.check_visible(tab.tab_component)));
		this.should_show_dropdown = ko.pureComputed(() => this.dropdown_tabs_visible().length > 0);
		this.is_dropdown_active = ko.pureComputed(() => this.dropdown_tabs_visible()
			.some(tab => tab.tab_component_name === this.selected_tab()));
		this.selected_dropdown_tab_label = ko.pureComputed(() =>
		{
			const selected = this.dropdown_tabs_visible().find(tab => tab.tab_component_name === this.selected_tab());
			return selected ? selected.tab_component_name : 'More';
		});

		// When item changes, validate the selected tab is still visible
		this.item.subscribe(() => {
			let current = this.selected_tab();
			let tabs = this.tabs();
			let currentTab = tabs.find(t => t.tab_component_name === current);
			if (currentTab && !this.check_visible(currentTab.tab_component))
			{
				let firstVisible = tabs.find(t => this.check_visible(t.tab_component));
				if (firstVisible) { this.select_tab(firstVisible.tab_component_name); }
			}
		});

		this.init();
	}

	init()
	{	
		const map = Grape.registry.getRegister(this.tabs_source());

		if (map && map.size)
		{
			const tabs = [...map.keys()].map(key => ({
				tab_component_name: key,
				tab_component: map.get(key)
			}));

			tabs.sort((a, b) =>
			{
				let a_idx = a.tab_component.idx || 99;
				let b_idx = b.tab_component.idx || 99;

				return a_idx < b_idx ? -1 : (a_idx > b_idx ? 1 : 0);
			});
			this.tabs(tabs);

			let last_tab = this.get_last_tab();
			const tab = tabs.find(tab => tab.tab_component_name === last_tab);
			if (last_tab && tab && this.check_visible(tab?.tab_component ?? null))
				this.select_tab(last_tab);
			else
			{
				const first_visible = tabs.find(tab => this.check_visible(tab.tab_component));
				if (first_visible) { this.select_tab(first_visible.tab_component_name); }
			}
		}
	}

	is_side_dropdown(tab)
	{
		const side_dropdown = tab?.['side-dropdown'];
		if (side_dropdown instanceof Function)
		{
			const item = this.item();
			return item ? side_dropdown(item) === true : false;
		}
		return side_dropdown === true;
	}

	select_tab(tab_component_name)
	{
		this.selected_tab(tab_component_name);
		this.dropdown_open(false);
		window.localStorage.setItem(`tabs_dashboard.${this.tabs_source()}.last_tab`, tab_component_name);
	}

	toggle_dropdown()
	{
		this.dropdown_open(!this.dropdown_open());
	}

	check_visible(tab)
	{
		if (tab?.visible instanceof Function)
		{
			const item = this.item();
			return item ? (tab.visible(item) ? true : false) : false;
		}
		else { return true; }
	}

	get_last_tab ()
	{
		const last_tab = window.localStorage.getItem(`tabs_dashboard.${this.tabs_source()}.last_tab`);
		return last_tab;
	}
}

export default {
	name: 'ko-tabs-dashboard',
	viewModel: TabsDashboardComponentViewModel,
	module_type: 'ko',
	template: template
};
